import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { formatDate } from "@/lib/dates";
import { fmtMoney } from "@/lib/time";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/i18n";

const STATUS_BADGE = {
  Draft: "bg-slate-100 text-slate-600 border-slate-200",
  Issued: "bg-sky-50 text-sky-800 border-sky-200",
  Paid: "bg-green-50 text-green-700 border-green-200",
  Cancelled: "bg-red-50 text-red-700 border-red-200",
};

const FILTERS = ["All", "Draft", "Issued", "Paid", "Cancelled"];

export default function Invoices() {
  const { t } = useT();
  const [invoices, setInvoices] = useState(null);
  const [filter, setFilter] = useState("All");
  const [q, setQ] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [testCount, setTestCount] = useState(0);
  const [matters, setMatters] = useState([]);
  const [matterId, setMatterId] = useState("");
  const navigate = useNavigate();

  const load = () => {
    const params = {};
    if (filter !== "All") params.status = filter;
    if (q.trim()) params.q = q.trim();
    api.get("/invoices", { params }).then((r) => setInvoices(r.data)).catch(() => {});
  };

  useEffect(load, [filter, q]);

  const loadTestCount = () => {
    api.get("/maintenance/test-invoice-count").then((r) => setTestCount(r.data.count)).catch(() => {});
  };
  useEffect(loadTestCount, []);

  const purgeTestInvoices = async () => {
    if (!window.confirm(t("invoices.purge_confirm", { count: testCount }))) return;
    try {
      const r = await api.post("/maintenance/purge-test-invoices", {});
      toast.success(t("invoices.purged", { count: r.data.deleted }));
      loadTestCount();
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openDialog = () => {
    api.get("/matters").then((r) => setMatters(r.data)).catch(() => {});
    setMatterId("");
    setDialogOpen(true);
  };

  return (
    <div data-testid="invoices-page" className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl tracking-tight text-slate-900">{t("invoices.title")}</h1>
          <p className="text-sm text-slate-500 mt-1">{t("invoices.subtitle")}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {testCount > 0 && (
            <Button variant="outline" data-testid="purge-test-invoices-button" onClick={purgeTestInvoices}
              className="text-red-700 border-red-200 hover:bg-red-50">
              <Trash2 className="h-4 w-4 mr-1.5" /> {t("invoices.purge_test")} ({testCount})
            </Button>
          )}
          <Button data-testid="create-invoice-button" onClick={openDialog} className="bg-slate-900 hover:bg-slate-800">
            <Plus className="h-4 w-4 mr-1.5" /> {t("invoices.new")}
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex gap-2 flex-wrap" data-testid="invoice-status-filters">
          {FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`invoice-filter-${f.toLowerCase()}`}
              onClick={() => setFilter(f)}
              className={`px-3.5 py-1.5 text-xs font-semibold rounded-full border transition-colors ${
                filter === f
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
              }`}
            >
              {f === "All" ? t("invoices.filter_all") : t(`val.${f}`)}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            data-testid="invoice-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("invoices.search_ph")}
            className="pl-9 pr-4 h-9 w-64 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-800"
          />
        </div>
      </div>

      {!invoices ? (
        <div className="text-sm text-slate-500" data-testid="invoices-loading">{t("invoices.loading")}</div>
      ) : invoices.length === 0 ? (
        <div className="text-sm text-slate-500" data-testid="invoices-empty">{t("invoices.empty")}</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto" data-testid="invoices-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">{t("invoices.col_number")}</th>
                <th className="px-4 py-3">{t("invoices.col_date")}</th>
                <th className="px-4 py-3">{t("invoices.col_client")}</th>
                <th className="px-4 py-3">{t("invoices.col_matter")}</th>
                <th className="px-4 py-3 text-right">{t("invoices.col_amount")}</th>
                <th className="px-4 py-3">{t("invoices.col_status")}</th>
                <th className="px-4 py-3">{t("invoices.col_due")}</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr
                  key={inv.id}
                  data-testid={`invoice-row-${inv.invoice_number}`}
                  onClick={() => navigate(`/invoices/${inv.id}`)}
                  className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3 font-semibold text-slate-800">{inv.invoice_number}</td>
                  <td className="px-4 py-3 text-slate-600">{formatDate(inv.invoice_date)}</td>
                  <td className="px-4 py-3 text-slate-600">{inv.client}</td>
                  <td className="px-4 py-3 text-slate-600 max-w-56 truncate">{inv.matter_name}</td>
                  <td className="px-4 py-3 text-right font-semibold text-slate-800">
                    {fmtMoney(inv.totals?.total)}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${STATUS_BADGE[inv.status] || ""}`}>
                      {t(`val.${inv.status}`)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{formatDate(inv.due_date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="new-invoice-matter-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">{t("invoices.new")}</DialogTitle>
            <DialogDescription>{t("invoices.select_matter")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t("invoices.matter")}</Label>
              <Select value={matterId} onValueChange={setMatterId}>
                <SelectTrigger data-testid="new-invoice-matter-select" className="mt-1.5">
                  <SelectValue placeholder={t("timer.select_matter")} />
                </SelectTrigger>
                <SelectContent>
                  {matters.map((m) => (
                    <SelectItem key={m.id} value={m.id} data-testid={`new-invoice-matter-option-${m.internal_ref || m.id}`}>
                      {m.name}
                      {m.internal_ref ? ` (${m.internal_ref})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              data-testid="new-invoice-continue-button"
              disabled={!matterId}
              onClick={() => navigate(`/invoices/new?matter=${matterId}`)}
              className="w-full bg-slate-900 hover:bg-slate-800"
            >
              {t("invoices.continue")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
